import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useSite } from "@/lib/site";
import { telHref } from "@/lib/api";

const STEPS = [
  { t: "Get Your Valuation", d: "Enter your registration and mileage to see an instant indicative valuation." },
  { t: "Send Your Enquiry", d: "Happy with the estimate? Provide your contact details so we can review your vehicle." },
  { t: "We'll Be in Touch", d: "If your car is of interest, we'll contact you directly to discuss the next steps." },
];

export const HowItWorks = () => (
  <section id="how-it-works" className="scroll-mt-24 border-b border-border bg-brand-offwhite py-16" data-testid="how-it-works-section">
    <div className="mx-auto max-w-6xl px-4 sm:px-6">
    <h2 className="text-2xl font-bold text-brand-navy sm:text-3xl">How it works</h2>
    <ol className="mt-8 grid gap-6 md:grid-cols-3 md:gap-8">
      {STEPS.map((s, i) => (
        <li key={s.t} className="rounded-md border border-border bg-white p-5" data-testid={`how-step-${i + 1}`}>
          <span className="inline-flex h-8 w-8 items-center justify-center rounded-full border-2 border-brand-red text-sm font-bold text-brand-red">{i + 1}</span>
          <p className="mt-3 text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">Step {i + 1}</p>
          <h3 className="mt-1 text-lg font-semibold text-brand-navy">{s.t}</h3>
          <p className="mt-2 text-slate-700">{s.d}</p>
        </li>
      ))}
    </ol>
    </div>
  </section>
);

const POINTS = [
  { t: "Sell outright or part exchange", d: "Sell your car to us, or ask about putting it towards a car from our stock." },
  { t: "The online estimate is provisional", d: "It's an indicative figure based on your registration, mileage and market data – not an offer." },
  { t: "The final offer depends on the car", d: "We'll make any offer after reviewing your details and inspecting the vehicle." },
  { t: "Appointments are arranged with us", d: "Our team will contact you to arrange a convenient time at the dealership." },
];

export const GoodToKnow = () => (
  <section className="py-16" data-testid="good-to-know-section">
    <h2 className="text-2xl font-bold text-slate-900 sm:text-3xl">Good to know</h2>
    <dl className="mt-8 grid gap-x-10 gap-y-6 sm:grid-cols-2">
      {POINTS.map((p) => (
        <div key={p.t}><dt className="font-bold text-slate-900">{p.t}</dt><dd className="mt-1 text-slate-700">{p.d}</dd></div>
      ))}
    </dl>
  </section>
);

const FAQS = [
  ["How does the online estimate work?", "When online estimates are available, we send the registration and mileage you enter to our vehicle data provider, DealerPricing, and show you an indicative figure based on the data returned, with the assumptions beside it. We don't charge you for this."],
  ["Is the estimate a guaranteed offer?", "No. It's a provisional guide. Any offer is made by Harrington's Direct after we've reviewed your details and inspected the car, and it may differ from the online estimate."],
  ["What happens after I submit my enquiry?", "Your enquiry comes straight to our team in Shenley. We'll review your details and contact you directly by phone or email if we'd like to take things further. Submitting an enquiry doesn't commit you to anything."],
  ["Should I mention damage, faults or outstanding finance?", "Yes, please. Use the optional comments box to tell us about any damage, warning lights, faults or outstanding finance – it helps us review your car properly and avoids surprises later."],
  ["Can I bring my car to the dealership?", "Please wait until we've contacted you before visiting the dealership, so we can review your vehicle and save you an unnecessary journey."],
];

export const Faqs = () => (
  <section id="faqs" className="scroll-mt-24 border-t border-border py-16" data-testid="faqs-section">
    <h2 className="text-2xl font-bold text-slate-900 sm:text-3xl">FAQs</h2>
    <Accordion type="single" collapsible className="mt-6 max-w-3xl">
      {FAQS.map(([q, a], i) => (
        <AccordionItem key={q} value={`faq-${i}`}>
          <AccordionTrigger className="text-left text-base font-semibold" data-testid={`faq-trigger-${i}`}>{q}</AccordionTrigger>
          <AccordionContent className="text-base text-slate-700">{a}</AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  </section>
);

export const Contact = () => {
  const { cfg } = useSite();
  const b = cfg.business;
  return (
    <section id="contact" className="scroll-mt-24 border-t border-border py-16" data-testid="contact-section">
      <h2 className="text-2xl font-bold text-slate-900 sm:text-3xl">Contact us</h2>
      <div className="mt-6 grid gap-6 sm:grid-cols-3">
        <div><p className="eyebrow">Dealership</p><p className="mt-2 text-slate-800"><span className="font-semibold">{b.trading_name}</span><br />{b.address}</p></div>
        <div><p className="eyebrow">Telephone</p><p className="mt-2"><a className="link text-lg" href={telHref(b.phone)} data-testid="contact-phone-link">{b.phone}</a></p></div>
        <div><p className="eyebrow">Email</p><p className="mt-2"><a className="link break-all" href={`mailto:${b.email}`} data-testid="contact-email-link">{b.email}</a></p></div>
      </div>
      {b.opening_hours && <p className="mt-6 text-slate-700 whitespace-pre-line">{b.opening_hours}</p>}
    </section>
  );
};
